import axios from 'axios';
import { auth } from './firebase'; // Adjust path to match your project structure
import * as Sentry from '@sentry/react';
import { ATLAS_PLAYWRIGHT_AUTH_DISABLED, getPlaywrightIdToken } from './devAuth';

// Get the API URLs from environment variables
const API_URL = process.env.REACT_APP_API_URL;
const ADVISOR_API_URL = process.env.REACT_APP_ADVISOR_API_URL;
const PDF_SERVICE_URL = process.env.REACT_APP_PDF_SERVICE_URL || 'http://localhost:8001';

// Create an Axios instance with the base URL
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true // Include credentials (cookies) if needed
});

// Add a request interceptor to include the Firebase token
api.interceptors.request.use(
  async (config) => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      config.headers['Authorization'] = `Bearer ${getPlaywrightIdToken()}`;
    } else if (auth.currentUser) {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(); // getIdToken is a method on the User object
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to capture errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      if (status >= 400 && status < 600) {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            status_code: status,
            url: error.config?.url,
            method: error.config?.method
          },
          extra: {
            response: error.response?.data,
            request: {
              url: error.config?.url,
              method: error.config?.method,
              data: error.config?.data,
              headers: error.config?.headers
            }
          }
        });
      }
    }
    return Promise.reject(error);
  }
);

// Create a separate instance for the advisor API
const advisor_api = axios.create({
  baseURL: ADVISOR_API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Optionally add token to advisor_api as well
advisor_api.interceptors.request.use(
  async (config) => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      config.headers['Authorization'] = `Bearer ${getPlaywrightIdToken()}`;
    } else if (auth.currentUser) {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to capture errors for advisor_api
advisor_api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      if (status >= 400 && status < 600) {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            status_code: status,
            url: error.config?.url,
            method: error.config?.method,
            api_type: 'advisor'
          },
          extra: {
            response: error.response?.data,
            request: {
              url: error.config?.url,
              method: error.config?.method,
              data: error.config?.data,
              headers: error.config?.headers
            }
          }
        });
      }
    }
    return Promise.reject(error);
  }
);

// Create a PDF service API instance
const pdf_api = axios.create({
  baseURL: PDF_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 120000 // 2 minute timeout for PDF generation
});

// Add Firebase token to PDF service API (same as main API)
pdf_api.interceptors.request.use(
  async (config) => {
    if (ATLAS_PLAYWRIGHT_AUTH_DISABLED) {
      config.headers['Authorization'] = `Bearer ${getPlaywrightIdToken()}`;
    } else if (auth.currentUser) {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for PDF service
pdf_api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      if (status >= 400 && status < 600) {
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            status_code: status,
            url: error.config?.url,
            method: error.config?.method,
            api_type: 'pdf_service'
          },
          extra: {
            response: error.response?.data,
            request: {
              url: error.config?.url,
              method: error.config?.method,
              data: error.config?.data,
              headers: error.config?.headers
            }
          }
        });
      }
    }
    return Promise.reject(error);
  }
);

// --- Nylas email/calendar connection (Phase 1: connect + read/draft) ---
// These hit realtorch-backend (the default `api` instance), which owns the
// per-user grant store. The Firebase token is attached by the interceptor above.
export const getNylasStatus = () =>
  api.get('/api/nylas/status').then((r) => r.data);

// A user can connect more than one mailbox: status returns {accounts, max_accounts}.
// loginHint steers Google/Microsoft to a specific account (adding a second one).
export const startNylasConnect = (provider, loginHint) =>
  api.post('/api/nylas/connect', {
    ...(provider ? { provider } : {}),
    ...(loginHint ? { login_hint: loginHint } : {}),
  }).then((r) => r.data);

// accountId: disconnect that account; omit to disconnect all of them.
export const disconnectNylas = (accountId) =>
  api.post('/api/nylas/disconnect', accountId != null ? { account_id: accountId } : {}).then((r) => r.data);

export const setDefaultNylasAccount = (accountId) =>
  api.post('/api/nylas/default', { account_id: accountId }).then((r) => r.data);

// --- Stripe billing portal (invoices, receipts, payment method) ---
// realtorch-backend creates a one-time portal session for the company's Stripe
// customer; only company owners and admins may open it.
export const createBillingPortalSession = ({ returnUrl, companyId } = {}) =>
  api.post('/api/stripe/customer-portal-session', {
    return_url: returnUrl,
    ...(companyId != null ? { company_id: companyId } : {}),
  }).then((r) => r.data);

export { api as default, advisor_api, pdf_api };
